import { Fragment, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { MANUALS, type Manual, type ManualSection } from "@/content/manuals";

/**
 * Manual de uso (CTA ou coletor): passos curtos com a captura de cada tela.
 * Fica fora do layout do sistema para abrir bem no celular e imprimir limpo.
 */

/** Converte **texto** em negrito; o resto fica como texto puro. */
function Rich({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, i) => (i % 2 === 1 ? <strong key={i} className="text-[#1B6E4F]">{part}</strong> : <Fragment key={i}>{part}</Fragment>))}
    </>
  );
}

function Section({ section }: { section: ManualSection }) {
  const mobile = section.image?.mobile;
  return (
    <section id={section.id} className="scroll-mt-20 bg-white rounded-2xl border border-[#E8EAED] p-5 sm:p-7 break-inside-avoid">
      <h2 className="text-lg sm:text-xl font-heading font-bold text-[#3F4A52]">{section.title}</h2>
      {section.intro && <p className="mt-2 text-[15px] text-[#3F4A52]/85 leading-relaxed"><Rich text={section.intro} /></p>}

      <div className={`mt-4 ${mobile ? "md:flex md:gap-8 md:items-start" : ""}`}>
        {section.image && (
          <figure className={mobile ? "md:w-[300px] shrink-0 mx-auto max-w-[320px]" : ""}>
            <img
              src={section.image.src}
              alt={section.image.alt}
              loading="lazy"
              className={`w-full border border-[#E8EAED] shadow-sm ${mobile ? "rounded-[22px]" : "rounded-xl"}`}
            />
          </figure>
        )}

        {section.steps && section.steps.length > 0 && (
          <ol className={`space-y-3 ${section.image ? (mobile ? "mt-5 md:mt-0 flex-1" : "mt-5") : ""}`}>
            {section.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-[#1B6E4F] text-white text-sm font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <span className="text-[15px] text-[#3F4A52] leading-relaxed pt-0.5"><Rich text={step.text} /></span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {section.tip && (
        <p className="mt-4 flex gap-2 text-sm text-[#1B6E4F] bg-[#1B6E4F]/8 border border-[#1B6E4F]/20 rounded-xl px-4 py-3">
          <i className="ri-lightbulb-line text-lg leading-5" aria-hidden="true" />
          <span><Rich text={section.tip} /></span>
        </p>
      )}
      {section.warning && (
        <p className="mt-4 flex gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line text-lg leading-5" aria-hidden="true" />
          <span><Rich text={section.warning} /></span>
        </p>
      )}
    </section>
  );
}

export default function ManualPage() {
  const { slug } = useParams<{ slug: string }>();
  const manual: Manual | undefined = slug === "cta" || slug === "coletor" ? MANUALS[slug] : undefined;

  useEffect(() => {
    if (manual) document.title = `FloraIQ — ${manual.title}`;
  }, [manual]);

  if (!manual) {
    return (
      <div className="p-8 text-center">
        <p className="text-[#3F4A52]">Manual não encontrado.</p>
        <Link to="/" className="text-[#1B6E4F] underline">Voltar ao início</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7] print:bg-white">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#E8EAED] print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-2.5 flex items-center gap-3">
          <Link
            to={manual.backTo}
            className="h-9 px-3 rounded-lg border border-[#E8EAED] hover:bg-[#F4F5F7] flex items-center gap-1.5 text-sm font-medium text-[#3F4A52] shrink-0"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            <span className="hidden sm:inline">{manual.backLabel}</span>
            <span className="sm:hidden">Voltar</span>
          </Link>
          <p className="text-sm font-heading font-bold text-[#3F4A52] truncate flex-1">{manual.title}</p>
          <button
            onClick={() => window.print()}
            className="h-9 px-3 rounded-lg border border-[#E8EAED] hover:bg-[#F4F5F7] hidden sm:flex items-center gap-1.5 text-sm text-[#3F4A52]"
          >
            <i className="ri-printer-line" aria-hidden="true" />
            Imprimir
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 sm:py-10 space-y-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#1B6E4F] text-white flex items-center justify-center shrink-0">
            <i className={`${manual.slug === "cta" ? "ri-plant-line" : "ri-smartphone-line"} text-2xl`} aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#3F4A52] leading-tight">{manual.title}</h1>
            <p className="mt-1 text-[15px] text-[#3F4A52]/80">{manual.subtitle}</p>
          </div>
        </div>

        <nav className="bg-white rounded-2xl border border-[#E8EAED] p-5 print:hidden">
          <p className="text-xs font-bold uppercase tracking-wide text-[#3F4A52]/60 mb-2">Neste manual</p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {manual.sections.map((sec) => (
              <li key={sec.id}>
                <a href={`#${sec.id}`} className="text-sm text-[#1B6E4F] hover:underline">{sec.title}</a>
              </li>
            ))}
          </ul>
        </nav>

        {manual.sections.map((sec) => <Section key={sec.id} section={sec} />)}

        <div className="text-center pt-2 pb-8 print:hidden">
          <Link
            to={manual.backTo}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            {manual.backLabel}
          </Link>
        </div>
      </main>
    </div>
  );
}
